import { UseQueryOptions } from '@tanstack/react-query';

import {
	ApplicationApi,
	ConsentDto,
	CountryInfoDto,
	SelectItemDto,
	StatesAndProvinces,
	StatesAndProvincesDto,
	SupportedCountryCodeDto,
	SupportedLanguageDto,
	TelephoneCountryCodeDto,
} from 'api';
import { cacheTime, infinityCacheTime } from 'api/ReactQueryOptions';

export const notificationLanguagesQuery = (api: ApplicationApi) => ({
	queryKey: configurationKeys.notificationLanguages(),
	queryFn: async () => (await api.getSupportedLanguages()).data,
	...infinityCacheTime<SupportedLanguageDto[]>(),
});

export const applicableConsentsQuery = (
	api: ApplicationApi,
	languageCode: string
) => ({
	queryKey: configurationKeys.applicableConsents(languageCode),
	queryFn: async () =>
		(await api.getApplicableConsents({ languageCode })).data,
	...infinityCacheTime<ConsentDto[]>(),
});

export const telephoneCountryCodesQuery = (api: ApplicationApi) => ({
	queryKey: configurationKeys.telephoneCountryCodes(),
	queryFn: async () => (await api.getTelephoneCountryCodes()).data,
	...infinityCacheTime<TelephoneCountryCodeDto[]>(),
});

export const recentlyVisitedQuery = (api: ApplicationApi) => ({
	queryKey: configurationKeys.recentlyVisited(),
	queryFn: async () => (await api.getRecentlyVisited()).data,
});

export const trainingsQuery = (api: ApplicationApi) => ({
	queryKey: configurationKeys.trainings(),
	queryFn: async () => (await api.getTrainings()).data,
});

export const releaseNotesListQuery = (api: ApplicationApi) => ({
	queryKey: configurationKeys.releaseNotesList(),
	queryFn: async () => (await api.getReleaseNotes()).data,
});

export const releaseNoteContentQuery = (api: ApplicationApi, id: number) => ({
	queryKey: configurationKeys.releaseNoteContent(id),
	queryFn: async () => (await api.getReleaseNoteConetnt(id)).data,
});

export const releaseNoteNotificationQuery = (api: ApplicationApi) => ({
	queryKey: configurationKeys.releaseNoteNotification(),
	queryFn: async () => (await api.getReleaseNoteNotificationStatus()).data,
	...cacheTime(60),
});

type QueryCountryInfoKeyType = ReturnType<
	typeof configurationKeys.countryInfos
>;
type QueryCountryECMInfoKeyType = ReturnType<
	typeof configurationKeys.countryECMInfos
>;
type QueryStatesAndProvincesKeyType = ReturnType<
	typeof configurationKeys.statesAndProvinces
>;

export const countryInfosQuery = (
	api: ApplicationApi,
	languageCode: string,
	qaAccessForm = false
): UseQueryOptions<
	CountryInfoDto[],
	Error,
	CountryInfoDto[],
	QueryCountryInfoKeyType
> => ({
	queryKey: configurationKeys.countryInfos(languageCode),
	queryFn: async (): Promise<CountryInfoDto[]> => {
		const response = qaAccessForm
			? await api.getCountryListForQaAccess({
					languageCountryCode: languageCode,
				})
			: await api.getCountryList({
					languageCountryCode: languageCode,
				});

		return response.data;
	},
	select: (data) => {
		const sorted = [...data].sort((a, b) =>
			(a.translatedCountryName || '').localeCompare(
				b.translatedCountryName || ''
			)
		);
		return sorted;
	},
	...infinityCacheTime<CountryInfoDto[], QueryCountryInfoKeyType>(),
});

//

export const countryInfosECMQuery = (
	api: ApplicationApi
): UseQueryOptions<
	SupportedCountryCodeDto[],
	Error,
	SupportedCountryCodeDto[],
	QueryCountryECMInfoKeyType
> => ({
	queryKey: configurationKeys.countryECMInfos(),
	queryFn: async () => (await api.getSupportedCountryCodes()).data,
	select: (data) => {
		const sorted = [...data].sort((a, b) =>
			(a.countryName || '').localeCompare(b.countryName || '')
		);
		return sorted;
	},
	...infinityCacheTime<
		SupportedCountryCodeDto[],
		QueryCountryECMInfoKeyType
	>(),
});

export const getStatesAndProvincesQuery = (
	api: ApplicationApi,
	statesAndProvinces: StatesAndProvinces
): UseQueryOptions<
	StatesAndProvincesDto[],
	Error,
	StatesAndProvincesDto[],
	QueryStatesAndProvincesKeyType
> => ({
	queryKey: configurationKeys.statesAndProvinces(statesAndProvinces),
	queryFn: async () =>
		(await api.getStatesAndProvincesList({ statesAndProvinces })).data,
	select: (data) => {
		const sorted = [...data].sort((a, b) => a.name.localeCompare(b.name));
		return sorted;
	},
	...infinityCacheTime<
		StatesAndProvincesDto[],
		QueryStatesAndProvincesKeyType
	>(),
});

export const hullIdStandardQuery = (
	api: ApplicationApi,
	countryCode: string
) => ({
	queryKey: configurationKeys.hullIdStandard(countryCode),
	queryFn: async () => (await api.getHullIdStandards({ countryCode })).data,
	...infinityCacheTime<SelectItemDto[]>(),
});

export const weightUnitsQuery = (api: ApplicationApi, countryCode: string) => ({
	queryKey: configurationKeys.weightUnits(countryCode),
	queryFn: async () => (await api.getWeightUnits({ countryCode })).data,
	...infinityCacheTime<SelectItemDto[]>(),
});

export const lengthUnitsQuery = (api: ApplicationApi, countryCode: string) => ({
	queryKey: configurationKeys.lengthUnits(countryCode),
	queryFn: async () => (await api.getLengthUnits({ countryCode })).data,
	...infinityCacheTime<SelectItemDto[]>(),
});

export const configurationKeys = {
	all: ['configuration'] as const,
	lengthUnits: (countryCode: string) =>
		[...configurationKeys.all, 'length-units', countryCode] as const,
	weightUnits: (countryCode: string) =>
		[...configurationKeys.all, 'weight-units', countryCode] as const,
	hullIdStandard: (language: string) =>
		[...configurationKeys.all, 'hullIdStandard', language] as const,
	countryInfos: (language: string) =>
		[...configurationKeys.all, 'countryInfos', language] as const,
	countryECMInfos: () =>
		[...configurationKeys.all, 'countryECMInfos'] as const,
	statesAndProvinces: (statesAndProvinces: StatesAndProvinces) =>
		[
			...configurationKeys.all,
			'statesAndProvinces',
			statesAndProvinces,
		] as const,
	notificationLanguages: () =>
		[...configurationKeys.all, 'notificationLanguages'] as const,
	applicableConsents: (language: string) =>
		[...configurationKeys.all, 'applicableConsents', language] as const,
	telephoneCountryCodes: () =>
		[...configurationKeys.all, 'telephoneCountryCodes'] as const,
	recentlyVisited: () =>
		[...configurationKeys.all, 'recentlyVisited'] as const,
	trainings: () => [...configurationKeys.all, 'trainings'] as const,
	releaseNotesList: () =>
		[...configurationKeys.all, 'releaseNotes', 'releaseNotesList'] as const,
	releaseNoteContent: (id: number) =>
		[
			...configurationKeys.all,
			'releaseNotes',
			'releaseNoteContent',
			id,
		] as const,
	releaseNoteNotification: () =>
		[...configurationKeys.all, 'releaseNoteNotification'] as const,
};
